import { Fieldset } from "primereact/fieldset";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Link } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CALC_TARIFF_POSTAL_SERVICES_LOAD,
  SET_STATUSSES_MONITORS,
  SET_STATUSSES_PROCESS
} from "../../constants/actionTypes";
import DialogEdit from "./components/DialogEdit";
import DialogEditList from "./components/DialogEditList";
import OrderHistory from "./components/OrderHistory";
import VirtualScrollDropdown from "../Core/virtual-scroll-dropdown/VirtualScrollDropdown";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import agent from "../../agent";
import styles from "./styles.module.scss";

const initialFilters = {
  ps: null,
  status: null,
  statusAdmin: null,
  orderNum: null,
};

const initialPageConfig = {
  page: 1,
  pageSize: 10,
  totalElements: 0
};

const modals = ['edit', 'edit-many'];
const modificationKeys = ['statusAdmin', 'commentClientId', 'resolvedDate', 'problemSituationId'];

const StatusMonitoring = () => {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const {postalServices} = useSelector((state) => state?.calcTariff);
  const {statusesMonitors, statusesProcessing} = useSelector((state) => state?.orderStatus);
  const [filters, setFilters] = useState(initialFilters);
  const [pageConfig, setPageConfig] = useState(initialPageConfig);
  const [openedModal, setOpenedModal] = useState('');
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [editOrder, setEditOrder] = useState(null);
  const [isHistoryDialog, setIsHistoryDialog] = useState(false);
  const [recordsToUpdate, setRecordsToUpdate] = useState([]);
  const [orderNum, setOrderNum] = useState("");
  const [comments, setComments] = useState([]);
  const [problemSituations, setProblemSituations] = useState([]);

  const fetchOptions = async () => {
    const postalServices = await agent.CalcTariff.getPostalServices();
    const statuses = await agent.StatusesMonitors.getTimelineStatusses();
    const statusesAdmin = await agent.StatusesMonitors.getAdminStatusses();
    dispatch({ type: CALC_TARIFF_POSTAL_SERVICES_LOAD, payload: postalServices });
    dispatch({ type: SET_STATUSSES_MONITORS, payload: statuses });
    dispatch({ type: SET_STATUSSES_PROCESS, payload: statusesAdmin });
  };

  useAsyncEffect(async () => {
    await fetchOptions();
  }, []);

  useAsyncEffect(async () => {
    await fetchOrders(filters, 0, pageConfig.pageSize);
  }, []);

  useEffect(() => {
    if (editOrder) {
      if (!isHistoryDialog) {
        setOpenedModal(modals[0]);
      }
    } else {
      setOpenedModal("");
    }
  }, [editOrder, isHistoryDialog]);

  const handleFilters = (key, value) => {
    setFilters({...filters, [key]: value});
  };

  const handleClean = async () => {
    setFilters(initialFilters);

    await fetchOrders({
      carrier: null,
      status: null,
      statusAdmin: null
    }, 0, pageConfig.pageSize);
  };

  const handleFind = async () => {
    await fetchOrders(filters, 0, pageConfig.pageSize);
  };

  const fetchOrders = async (options, page = 0, pageSize = 10) => {
    const data = await agent.StatusesMonitors.getOrdersList({
      status: options.status ?? "0",
      statusAdmin: options.statusAdmin ?? "0",
      ps: options.ps ?? "",
      orderNum: options.orderNum ?? ""
    }, page, pageSize);

    setPageConfig({
      ...pageConfig,
      page: data?.number,
      pageSize: data?.size,
      totalElements: data?.totalElements
    });
    setOrders(data?.content || []);
    setSelectedOrders([]);
  };

  const handlePageChange = async (event) => {
    await fetchOrders(filters, event.page, event.rows);
  };

  const parseDate = (value) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    if (typeof value === "string") {
      const dateMatcher = value.match(/^(\d{2})\.(\d{2})\.(\d{4})/);

      if (dateMatcher) {
        return new Date(+dateMatcher[3], +dateMatcher[2] - 1, +dateMatcher[1]);
      }

      const date = new Date(value);

      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  };

  const sameValue = (orig, next, key) => {
    if (key === "resolvedDate") {
      const a = parseDate(orig);
      const b = parseDate(next);

      return (a ? a.getTime() : null) === (b ? b.getTime() : null);
    }

    return (orig ?? null) === (next ?? null);
  };

  const handleRecordUpdates = (id, key, value) => {
    if (key === "id") {
      return;
    }

    setRecordsToUpdate(prev => {
      const original = orders.find(order => order.id === id) || {};
      const existing = prev.find(record => record.id === id) || { id };
      const next = { ...existing };

      if (sameValue(original[key], value, key)) {
        delete next[key];
      } else {
        next[key] = value;
      }

      const hasChanges = Object.keys(next).some((key) => key !== "id");
      const withoutThis = prev.filter((r) => r.id !== id);
      return hasChanges ? [...withoutThis, next] : withoutThis;
    });
  };

  const psMap = useMemo(() => {
    const map = new Map();

    postalServices.forEach(ps => map.set(ps.code, ps.nameRu));

    return map;
  }, [postalServices]);

  const tableData = useMemo(() => {
    const stagedById = new Map(recordsToUpdate.map(({ id, ...rest }) => [id, rest]));

    return orders.map(order => {
      const staged = stagedById.get(order.id) || {};
      const pick = (key, fb) => Object.hasOwn(staged, key) ? staged[key] : fb;

      const statusAdmin = pick("statusAdmin", order.statusAdmin ?? null);
      const commentClient = comments
          .find(comment => comment.id === (staged.commentClientId ?? order.commentClientId))
          ?.nameRu;
      const problemSituation = problemSituations
          .find(situation => situation.id === (staged.problemSituationId ?? order.problemSituationId))
          ?.nameRu;

      let resolvedDate = pick("resolvedDate", order.resolvedDate ?? null);
      resolvedDate = resolvedDate ? parseDate(resolvedDate) : null;

      return {
        ...order,
        ...staged,
        statusAdmin,
        commentClient,
        problemSituation,
        resolvedDate,
        psName: psMap.get(order.psId) ?? ""
      };
    });
  }, [orders, recordsToUpdate, comments, problemSituations, psMap]);

  const tableById = useMemo(() => new Map(
      tableData.map(r => [r.id, r])), [tableData]
  );

  const tableBodyAdminStatus = ({ id, statusAdmin }) => (
      <Dropdown
          value={ statusAdmin ?? null }
          options={statusesProcessing || []}
          optionLabel="nameRu"
          optionValue="id"
          placeholder="Выберите статус"
          showClear
          onChange={(e) => handleRecordUpdates(id, "statusAdmin", e.value ?? null)}
      />
  );

  const tableBodyClientComment = ({ id, commentClient, commentClientId }) => (
      <VirtualScrollDropdown code="comment-client"
                             searchCode="comment"
                             id={id}
                             initialValue={commentClientId}
                             initialLabel={commentClient}
                             placeholder="Выберите комментарий"
                             onChange={handleRecordUpdates}
                             setCatalogOptions={setComments}
      />
  )

  const tableBodyProblemSituation = ({ id, problemSituation, problemSituationId }) => (
      <VirtualScrollDropdown code="problem-situation"
                             searchCode="problem-situation"
                             initialValue={problemSituationId}
                             initialLabel={problemSituation}
                             id={id}
                             placeholder="Выберите проблемную ситуацию"
                             onChange={handleRecordUpdates}
                             setCatalogOptions={setProblemSituations}
      />
  );

  const tableBodySolutionDate = ({ id, resolvedDate }) => (
      <Calendar value={resolvedDate}
                onChange={(e) => handleRecordUpdates(id, 'resolvedDate', e.value || null)}
                locale="ru"
                showIcon
                showButtonBar
                onClearButtonClick={() => handleRecordUpdates(id, 'resolvedDate', null)}
      />
  );

  const buildFullRowUpdate = useCallback((id, staged) => {
    const current = tableById.get(id);
    const pick = (key) => Object.hasOwn(staged ?? {}, key) ? staged[key] : current[key];

    const commentClient = pick("commentClientId") !== 0 ? pick("commentClientId") : null;
    const statusAdmin = pick("statusAdmin") ?? null;
    const problemSituation = pick("problemSituationId") !== 0 ? pick("problemSituationId") : null;
    const resolvedDate = pick("resolvedDate") ?? null;

    return {
      id,
      commentId: commentClient,
      problemSituationId: problemSituation,
      statusAdmin: statusAdmin,
      resolvedDate: parseDate(resolvedDate)
    };
  }, [tableById]);

  const selectedIds = useMemo(() => selectedOrders.map(order => order.id), [selectedOrders]);
  const pageIds = useMemo(() => tableData.map(item => item.id), [tableData]);

  const allRowsSelected = useMemo(() => {
    if (!pageIds.length || selectedIds.length !== pageIds.length) {
      return false;
    }

    return pageIds.every(id => selectedIds.includes(id));
  }, [pageIds, selectedIds]);

  const sameCarrierOnPage = useMemo(() => {
    if (!tableData?.length) {
      return false;
    }

    const firstCarrier = tableData[0]?.psId ?? null;
    return tableData.every(row => row.psId === firstCarrier);
  }, [tableData]);

  const firstRowId = useMemo(() => (tableData?.[0]?.id ?? null), [tableData]);
  const firstRowStaged = useMemo(
      () => (recordsToUpdate ?? []).find(row => row.id === firstRowId) ?? null,
      [recordsToUpdate, firstRowId]
  );

  const hasFirstRowChanges = useMemo(() => {
    if (!firstRowStaged) {
      return false;
    }

    const hasOwn = Object.hasOwn
        ?? ((object, key) => Object.hasOwn(object, key));

    return modificationKeys.some((key) => hasOwn(firstRowStaged, key));
  }, [firstRowStaged]);

  const canBulkSave = allRowsSelected && sameCarrierOnPage && hasFirstRowChanges;

  const handleSaveSelected = useCallback(async () => {
    if (!canBulkSave) {
      let reason;

      if (!allRowsSelected) {
        reason = "Выберите все записи на странице.";
      } else {
        if (!sameCarrierOnPage) {
          reason = "На странице должны быть записи с одинаковым перевозчиком.";
        } else {
          reason = "В первой строке нужно указать хотя бы одно поле.";
        }
      }

      showMessage("info", "Недостаточно данных", reason);
      return;
    }

    if (!firstRowStaged || (Object.keys(firstRowStaged).length === 1)) {
      showMessage("info","Недостаточно данных","В первой строке укажите хотя бы одно поле для изменения.");
      return;
    }

    const payload = selectedIds.map(id => buildFullRowUpdate(id, firstRowStaged));

    try {
      await agent.StatusesMonitors.setTimelineOrder({ updates: payload });

      showMessage("success", "Успешно", "Изменения применены ко всем выбранным записям.");
      setRecordsToUpdate(prev => (prev ?? []).filter(r => !selectedIds.includes(r.id)));

      await fetchOrders(filters, pageConfig.page, pageConfig.pageSize);
    } catch (err) {
      const message = err?.response?.text || err?.response?.body?.message || err?.message || "Не удалось сохранить изменения";

      showMessage("error", "Ошибка", message);
    }
  }, [
      allRowsSelected, sameCarrierOnPage, firstRowStaged,
      selectedIds, filters, pageConfig.page, buildFullRowUpdate
  ]);

  const tableBodyActions = useCallback((rowData) => {
    const openHistoryModal = (e) => {
      e.stopPropagation();

      setIsHistoryDialog(true);
      setOrderNum(rowData.orderNum);
    };

    const saveOrderData = async (e) => {
      e.stopPropagation();

      const staged = recordsToUpdate.find(record => record.id === rowData.id);

      if (!staged || Object.keys(staged).length === 1) {
        showMessage("info", "Нет изменений", "Изменения для строки отсутствуют.");
        return;
      }

      const payload = [buildFullRowUpdate(rowData.id, staged)];

      try {
        await agent.StatusesMonitors.setTimelineOrder({updates: payload});

        showMessage("success", "Успешно", `Статус для заказа ${rowData.orderNum} сохранён.`);
        setRecordsToUpdate(prev => (prev ?? []).filter(r => r.id !== rowData.id));

        await fetchOrders(filters, pageConfig.page, pageConfig.pageSize);
      } catch (err) {
        const message = err?.response?.text || err?.response?.body?.message || err?.message || "Не удалось сохранить изменения";

        showMessage("error", "Ошибка", message);
      }
    };

    const stagedUpdate = recordsToUpdate.find(record => record.id === rowData.id);
    const isSaveBlocked = !(stagedUpdate && Object.keys(stagedUpdate).some(key => key !== "id"));

    const buttons = [
        {
          onClick: openHistoryModal,
          icon: "stopwatch",
          tooltip: "История заказа",
          color: "#f3a60b",
          disabled: false
        },
        {
          onClick: saveOrderData,
          icon: "save",
          tooltip: "Сохранить",
          color: "#76c783",
          disabled: isSaveBlocked
        }
    ]

    return (
        <div className="p-d-flex p-jc-center">
          {buttons.map(({ onClick, icon, tooltip, color, disabled }, id) => (
              <Button key={id}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={onClick}
                      className="p-button-text p-button-rounded"
                      tooltip={tooltip}
                      tooltipOptions={{ position: "bottom" }}
                      style={{ width: "25px", ...(color ? { color: color } : null) }}
                      icon={`pi pi-${icon}`}
                      disabled={disabled}
                  />
              ))}
            </div>
        );
      },
      [setIsHistoryDialog, recordsToUpdate, buildFullRowUpdate, filters, pageConfig.page]
  );

  const tableBodyOrderNum = ({orderNum}) => <Link to={`/order/${orderNum}`} className="order__link">{orderNum}</Link>;

  const tableBodyStatus = ({ status }) => {
    const stat = statusesMonitors?.find((stat) => stat.id === status);
    return (
      <div className="p-text-normal">{stat?.nameRu}</div>
    );
  }

  const showMessage = (severity, summary, detail, life = 5000, sticky = false) => {
    toast.current.show({ severity, summary, detail, life, sticky });
  };

  const onSuccessEdit = ({ids, statusAdmin, comment}) => {
    setOrders(orders.map((order) => {
      const finOrder = ids.find((id) => id === order.id);
      if (finOrder) {
        return {...order, comment, statusAdmin}
      } else {
        return order;
      }
    }));
    setOpenedModal('');
  };

  return (
    <div className="p-mb-5">
      <Toast ref={toast} />
      {editOrder ?
        <DialogEdit
          viewDialog={openedModal === modals[0]}
          hideDialog={() => setEditOrder(null)}
          handleConfirm={onSuccessEdit}
          order={editOrder}
          showMessage={showMessage}
        /> : null
      }
      {openedModal === modals[1] ?
        <DialogEditList
          viewDialog={openedModal === modals[1]}
          hideDialog={() => setOpenedModal('')}
          handleConfirm={onSuccessEdit}
          list={selectedOrders}
          showMessage={showMessage}
        /> : null
      }
      <OrderHistory orderNum={orderNum} isHistoryDialog={isHistoryDialog} setIsHistoryDialog={setIsHistoryDialog} />
      <div className="p-d-flex p-jc-between p-ai-center">
        <h2>Мониторинг статусов</h2>
      </div>

      <Fieldset legend="Header" toggleable>
        <div className="p-fluid p-formgrid p-grid p-mb-4">
          <div className="p-col-3 p-mr-4 p-d-flex p-flex-column">
            <label className="p-mb-1" htmlFor="psId">Перевозчик</label>
            <Dropdown inputId="psId" value={filters.ps}
                      options={postalServices}
                      onChange={(e) => handleFilters('ps', e.value)}
                      optionLabel="nameRu"
                      optionValue="code"
                      showClear
            />
          </div>

          <div className="p-col-3 p-mr-4 p-d-flex p-flex-column">
            <label className="p-mb-1" htmlFor="status">Статус</label>
            <Dropdown inputId="status" value={filters.status}
                      options={statusesMonitors}
                      onChange={(e) => handleFilters('status', e.value)}
                      optionLabel="nameRu"
                      optionValue="id"
                      showClear
            />
          </div>

          <div className="p-col-3 p-mr-3 p-d-flex p-flex-column">
            <label className="p-mb-1" htmlFor="status-admin">Статус обработки</label>
            <Dropdown inputId="status-admin" value={filters.statusAdmin}
                      options={statusesProcessing}
                      onChange={(e) => handleFilters('statusAdmin', e.value)}
                      optionLabel="nameRu"
                      optionValue="id"
                      showClear
            />
          </div>

          <div className="p-col-3 p-my-3 p-d-flex p-flex-column">
            <label className="p-mb-1" htmlFor="order-num">Трек номер SLQ</label>
            <InputText
                id="order-num"
                value={filters.orderNum}
                onInput={(e) => handleFilters('orderNum', e.currentTarget.value)}
            />
          </div>
        </div>

        <div className="p-grid p-justify-end">
          <div className="p-mr-2">
            <Button className="p-button-warning" label="Сбросить" onClick={handleClean}/>
          </div>
          <div className="p-mr-2">
            <Button onClick={handleFind} label="Найти"/>
          </div>
        </div>

        <div className="p-d-flex p-ai-center" style={{ gap: "9px" }}>
          <Button
              onClick={() => fetchOrders(filters, 0, pageConfig.pageSize)}
              label="Обновить статус"
              className="p-button-sm"
              icon="pi pi-sync"
          />

          <Button
              onClick={handleSaveSelected}
              label="Сохранить"
              className={`p-button-sm ${styles.saveButton}`}
              icon="pi pi-save"
              disabled={!canBulkSave}
          />

          {selectedOrders.length ?
            <div className="p-ml-2 p-text-normal">
              Выбрано количество строк: {selectedOrders.length}
            </div> : null
          }
        </div>
      </Fieldset>
      <div className="datatable-doc-demo">
        <div className="card">
          <div className="card">
            <DataTable
                className="p-datatable-customers p-datatable-sm"
                selection={selectedOrders}
                onSelectionChange={(e) => setSelectedOrders(e.value)}
                value={tableData}
                emptyMessage="Данные не найдены"
                dataKey="id"
                rowHover
            >
              <Column className={styles}
                      selectionMode="multiple"
                      headerStyle={{ justifyContent: "center", width: '2.375rem' }}
              />
              <Column columnKey="orderNum"
                      header="Номер заказа"
                      body={tableBodyOrderNum}
              />
              <Column field="psName" columnKey="psName" header="Перевозчик" />
              <Column columnKey="status"
                      header="Статус"
                      body={tableBodyStatus}
              />
              <Column field="startDate" columnKey="startDate" header="Дата начала" style={{ wordSpacing: "100%" }} />
              <Column columnKey="statusAdmin"
                      header="Статус обработки"
                      body={tableBodyAdminStatus}
              />
              <Column field="commentOrder" columnKey="commentOrder" header="Комментарий внутренний" />
              <Column className={styles.clientCommentColumn}
                      columnKey="commentClientId"
                      header="Комментарий клиенту"
                      body={tableBodyClientComment}
              />
              <Column className={styles.problemSituationColumn}
                      columnKey="problemSituationId"
                      header="Проблемные ситуации"
                      body={tableBodyProblemSituation}
              />
              <Column field="adminStatusDate" columnKey="adminStatusDate" header="Дата обработки" />
              <Column columnKey="resolvedDate"
                      header="Дата решения проблемы"
                      body={tableBodySolutionDate}
                      style={{ minWidth: "10rem" }}
              />
              <Column field="id" columnKey="id" body={tableBodyActions}/>
            </DataTable>
          </div>
          <Paginator
            currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            first={pageConfig.page*pageConfig.pageSize}
            page={pageConfig.page}
            rows={pageConfig.pageSize}
            totalRecords={pageConfig.totalElements}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default StatusMonitoring;